import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import { type RowMetricPt, buildCharRunMap } from '@/lib/shapes/shape-text'
import {
  CANVAS_FONT_MIN_PT,
  letterSpacingCss,
  measureFontAscentDescentPt,
} from '@/lib/text/canvas-font-metrics'
import { NATURAL_LEADING_MULTIPLIER, naturalLeadingPt, parsedLeadingPt } from '@/lib/text/leading'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import { effectivePointSizeForRun } from '@/lib/text/text-run-vertical-align'
import type { FontInkRatioCache } from './types/font-ink-ratio-cache'
import { measureContext } from './handlers/shared-measure-context'

/**
 * Everything the shape-text scanline engine needs to turn paragraphs into a laid-out flow: the
 * per-run text measurer plus the frame-level type metrics derived from the first run.
 *
 * Extracted from `useShapeTextLayout` so the exact same measurement code path serves both
 * rendering *and* capacity measurement (`fitParagraphsToShape`). That shared path is the point:
 * if the linked-chain fitter measured differently from the renderer, a shape frame would hand off
 * overflow at one offset and draw its text to another (WMPCM-7988).
 */
export interface ShapeTextMeasurer {
  /** Width in points of `value`, styled by the run covering `charIndex` (frame defaults if
   *  omitted). Points, not CSS px: the shape geometry this feeds is in document points, so
   *  measuring in px would fit text against a ruler 4/3 too coarse. */
  measureTextPt: (value: string, charIndex?: number) => number
  charRunMap: Array<TextRun | null>
  pointSize: number
  /**
   * The row-height basis for `getShapeTextFlow`: the frame's LARGEST run point size (see
   * `rowMetricRun`), floored at `CANVAS_FONT_MIN_PT`. Deliberately not `pointSize` — rows are one
   * uniform height for the whole frame, so it has to clear the tallest run in it.
   */
  pointPt: number
  leadingMult: number
  /** How tall one row of a given run's text is — see `runRowMetricPt`. Carried on the measurer so
   *  every stage of one layout pass (fit, render, bake) resolves row heights identically, and so the
   *  underlying font-metric cache lives exactly as long as that pass. */
  rowMetricPt: RowMetricPt
  familyId: number | undefined
  cssFamily: string
  fontWeight: number
  fontStyle: string
}

/**
 * Resolve the leading multiplier for a run, as a ratio of its own point size.
 *
 * Default ("Metric"/auto) leading must match the text tool's natural line height so the same text
 * lays out identically in a shape text frame and a text-tool frame (WMPCM-8162). All of the stored
 * format's semantics — the "Metric" sentinel, the legacy ratio band, absolute pt values, and the
 * damage floor — live in `parsedLeadingPt` (paragraph-block-styles.ts), which this now defers to
 * rather than re-deriving them. The duplicated copy here predated that module and had already
 * drifted: it applied no floor, so an absolute leading left over from a smaller point size produced
 * a sub-line row pitch here too.
 */
function resolveLeadingMultiplier(run: TextRun | undefined): number {
  const pointSize = effectivePointSizeForRun(run)

  if (pointSize <= 0) return NATURAL_LEADING_MULTIPLIER

  return parsedLeadingPt(run?.leading, pointSize) / pointSize
}

/**
 * How tall one row of this run's text really is, in points.
 *
 * `naturalLeadingPt` assumes every face fits in `NATURAL_LEADING_MULTIPLIER` (1.2) em, and that
 * assumption is the whole of the model's vertical sizing — while the editor paints each run with its
 * own family, weight and **style** and lets the browser size the line box from the font's real
 * metrics (`shape-text-editor/components/shape-text-chunk.tsx`). A face whose `ascent + descent` exceeds 1.2 em therefore drew
 * ink taller than the box the model reserved for it, and spilled into the row below: QA's "different
 * fonts and sizes overlap each other" on text carrying no explicit leading at all, which is why the
 * WMPCM-9070 2026-08-30 fix (Known Gap #42, an *implicit-vs-explicit leading* fix) could not reach it.
 *
 * `measureFontAscentDescentPt` is the same helper the caret/ink box already uses (WMPCM-10123), and
 * its docstring names these as "the same font-wide metrics CSS `line-height: normal` uses, which is
 * how this editor renders text" — so this makes the two vertical decisions agree rather than
 * introducing a new notion of height.
 *
 * **Only ever taller.** The `Math.max` means a face at or under 1.2 em is byte-identical to before,
 * and jsdom — which implements no `fontBoundingBox*`, so the helper returns its 0.8/0.2 fallback
 * (1.0 em) — is identical too. Step 3b of this fix is therefore invisible to the suite in both
 * directions; it is browser-only behaviour.
 */
export function runRowMetricPt(
  run: TextRun | undefined,
  fonts?: ParagraphBlockFontLookup,
  cache?: FontInkRatioCache
): number {
  const pointSize = effectivePointSizeForRun(run)

  return Math.max(naturalLeadingPt(pointSize), fontInkRatio(run, fonts, cache) * pointSize)
}

export function createFontInkRatioCache(): FontInkRatioCache {
  return new Map()
}

function fontInkRatio(
  run: TextRun | undefined,
  fonts: ParagraphBlockFontLookup | undefined,
  cache?: FontInkRatioCache
): number {
  const key = `${run?.fontFamilyId ?? ''}|${run?.fontId ?? ''}`
  const cached = cache?.get(key)

  if (cached != null) return cached

  const pointSize = Math.max(1, effectivePointSizeForRun(run))
  const { ascentPt, descentPt } = measureFontAscentDescentPt(
    measureContext(),
    pointSize,
    run?.fontFamilyId,
    run?.fontId,
    fonts
  )
  const ratio = (ascentPt + descentPt) / pointSize
  const safe = Number.isFinite(ratio) && ratio > 0 ? ratio : NATURAL_LEADING_MULTIPLIER

  cache?.set(key, safe)

  return safe
}

/** The tallest `runRowMetricPt` across every run in `paragraphs`, or 0 when there are none. */
function maxRunRowMetricPt(
  paragraphs: Paragraph[],
  fonts: ParagraphBlockFontLookup | undefined,
  cache: FontInkRatioCache
): number {
  let tallest = 0

  for (const paragraph of paragraphs) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        tallest = Math.max(tallest, runRowMetricPt(run, fonts, cache))
      }
    }
  }

  return tallest
}

/**
 * The uniform row pitch `getShapeTextFlow` will slice this frame's outline at — i.e. exactly
 * `pointPt * leadingMult` for the measurer this list would build.
 *
 * Exported so the chain fitter can ask "would measuring a PREFIX of this list give a finer grid than
 * measuring the whole thing?" without rebuilding a measurer. That question is the gate on
 * `fit-paragraphs-to-shape.ts`'s grow pass: the pitch is a max over every run in the list handed in,
 * so a frame asked to fit the whole remaining story is measured against runs destined for frames
 * further down the chain (WMPCM-9070 Bug 4).
 */
export function gridRowPitchPt(paragraphs: Paragraph[], fonts?: ParagraphBlockFontLookup): number {
  const { pointPt, leadingMult } = rowGridBasisPt(paragraphs, fonts)

  return pointPt * leadingMult
}

/**
 * The `(pointPt, leadingMult)` pair on its own, for a caller that needs to slice a shape at the grid
 * ONE paragraph list implies while flowing a DIFFERENT one through it.
 *
 * That sounds like a contradiction of "measure with the engine that draws" and is the opposite: the
 * chain fitter is handed the whole remaining story but the frame only ever draws its own slice, so
 * asking "how much would fit if this frame were sliced the way it will actually be drawn?" needs the
 * basis and the content to come from different lists exactly once — see `growKeptOffset`
 * (`fit-paragraphs-to-shape.ts`), which uses the answer only as a search bound and verifies the
 * offset it finally accepts the ordinary way.
 */
export function rowGridBasisPt(
  paragraphs: Paragraph[],
  fonts?: ParagraphBlockFontLookup
): { pointPt: number; leadingMult: number } {
  return resolveRowGridBasis(paragraphs, fonts, createFontInkRatioCache())
}

/**
 * The run whose type metrics set the frame-wide row pitch: the one with the LARGEST point size.
 *
 * `getShapeTextFlow` slices the outline into rows of one uniform height before any text is placed,
 * so the frame gets exactly one pitch — but glyphs are measured and painted at their own run's size
 * (`measureTextPt`). Taking the pitch from `paragraphs[0].textLines[0].textRuns[0]`, as this used to,
 * meant restyling anything after the first run to a larger size kept the small pitch and drew the
 * big glyphs on top of each other: 72pt text on 14.4pt rows. That is WMPCM-9070 Bug 6 inside a shape.
 *
 * The largest run is the only choice that cannot overlap. The cost is that one oversized word spaces
 * out the whole frame — correct-but-loose beats unreadable, and it matches how the row engine is
 * built (see `getShapeTextFlow`, which takes a single `fontSizePt`).
 */
function rowMetricRun(paragraphs: Paragraph[]): TextRun | undefined {
  let widest: TextRun | undefined
  let widestPitchPt = -Infinity

  for (const paragraph of paragraphs) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        const pointSize = effectivePointSizeForRun(run)
        const pitchPt = parsedLeadingPt(run.leading, pointSize)

        if (pitchPt > widestPitchPt) {
          widestPitchPt = pitchPt
          widest = run
        }
      }
    }
  }

  return widest
}

/**
 * The `(pointPt, leadingMult)` pair `getShapeTextFlowBoundary` slices the outline with.
 *
 * `pointPt` is unchanged — the largest run's point size, floored — because it is only ever consumed
 * multiplied by `leadingMult` (verified: every call site pairs them). `leadingMult` is then raised,
 * never lowered, so the uniform grid clears the tallest row any run in the frame can actually
 * produce:
 *
 * - `rowMetricRun` selects the metric run by `parsedLeadingPt` but the basis then reads its
 *   `pointSize`, so a SMALL run carrying a large explicit leading could win the selection and hand
 *   back a pitch shorter than the frame's tallest glyph — defeating the anti-overlap guarantee
 *   `rowMetricRun`'s own docstring claims;
 * - and `runRowMetricPt` measures real font metrics, so a face whose ink exceeds 1.2 em is covered
 *   too (see `runRowMetricPt`).
 *
 * Raising it is the only safe direction. Lowering the grid pitch is what `rowMetricRun` exists to
 * prevent — it is what stopped 72pt glyphs drawing on 14.4pt rows — and the reclaim pass in
 * `layoutShapeTextWithPerLineLeading` relies on `row thickness <= grid pitch` to know that
 * re-stacking can only ever free space, never need more.
 */
function resolveRowGridBasis(
  paragraphs: Paragraph[],
  fonts: ParagraphBlockFontLookup | undefined,
  cache: FontInkRatioCache
): { pointPt: number; leadingMult: number } {
  const firstRun = paragraphs[0]?.textLines?.[0]?.textRuns?.[0]
  const metricRun = rowMetricRun(paragraphs) ?? firstRun
  const pointPt = Math.max(CANVAS_FONT_MIN_PT, effectivePointSizeForRun(metricRun))
  const leadingPitchPt = pointPt * resolveLeadingMultiplier(metricRun)
  const tallestRowPt = maxRunRowMetricPt(paragraphs, fonts, cache)

  return { pointPt, leadingMult: Math.max(leadingPitchPt, tallestRowPt) / pointPt }
}

export function buildShapeTextMeasurer(
  paragraphs: Paragraph[],
  fonts?: ParagraphBlockFontLookup
): ShapeTextMeasurer {
  const charRunMap = buildCharRunMap(paragraphs)
  const firstRun = paragraphs[0]?.textLines?.[0]?.textRuns?.[0]
  // Row PITCH comes from the largest run (see `rowMetricRun`/`resolveRowGridBasis`). The frame's
  // default point size stays the FIRST run's: it is the fallback both `measureTextPt` and the
  // editor's per-glyph spans (`shape-text-editor/components/shape-text-chunk.tsx`) use for a character with no run of its own,
  // so widening it here would silently draw unstyled text at the biggest size in the frame.
  const inkRatioCache = createFontInkRatioCache()
  const { pointPt, leadingMult } = resolveRowGridBasis(paragraphs, fonts, inkRatioCache)
  // An empty frame has no character to name a run, so it measures the seeded first run: the face the
  // next keystroke types in (WMPCM-11367).
  const rowMetricPt: RowMetricPt = (run) => runRowMetricPt(run ?? firstRun, fonts, inkRatioCache)
  const pointSize = firstRun?.pointSize ?? DEFAULT_POINT_SIZE
  const familyId = firstRun?.fontFamilyId
  const cssFamily =
    familyId && fonts?.getFamilyCssName(familyId)
      ? `"${fonts.getFamilyCssName(familyId)}", sans-serif`
      : 'sans-serif'
  const fontWeight =
    firstRun?.fontId != null && fonts?.getVariantByFontId(firstRun.fontId)?.bold ? 700 : 400
  const fontStyle =
    firstRun?.fontId != null && fonts?.getVariantByFontId(firstRun.fontId)?.italic
      ? 'italic'
      : 'normal'

  const measureTextPt = (value: string, charIndex?: number): number => {
    const ctx = measureContext()
    const run = charIndex != null ? (charRunMap[charIndex] ?? null) : null
    const runSize = effectivePointSizeForRun(run, pointSize)
    // Real rendered size, *not* `pointPt`: the `CANVAS_FONT_MIN_PT` floor exists to keep the
    // row-height basis sane (see `pointPt`), but the editor paints this run at its own size.
    // Applying the floor here too measured any run below that size wider than it draws, drifting
    // every caret on the row to the right of its glyph.
    const runPt = Math.max(0.5, runSize)
    const runFamilyId = run?.fontFamilyId ?? familyId
    const runCssFamily =
      runFamilyId && fonts?.getFamilyCssName(runFamilyId)
        ? `"${fonts.getFamilyCssName(runFamilyId)}", sans-serif`
        : cssFamily
    const runWeight =
      run?.fontId != null ? (fonts?.getVariantByFontId(run.fontId)?.bold ? 700 : 400) : fontWeight
    const runStyle =
      run?.fontId != null
        ? fonts?.getVariantByFontId(run.fontId)?.italic
          ? 'italic'
          : 'normal'
        : fontStyle

    // No 2D context (jsdom, or a canvas-less environment): fall back to a proportional estimate.
    // Deterministic, which is what makes the scanline fitter testable without real font metrics.
    if (!ctx) return value.length * Math.max(3, runPt * 0.5)
    // `${runPt}px`: 1 document point is 1 CSS px (see `canvas-font-metrics.ts`), so this measures
    // the run at the size it paints and returns points directly.
    ctx.font = `${runStyle} ${runWeight} ${runPt}px ${runCssFamily}`
    // After `font`, never before — the canvas `font` setter parses the CSS shorthand and resets
    // both of these to their initial values. Unkerned matches the paint
    // (`shape-text-editor/components/shape-text-chunk.tsx` sets `fontKerning: 'none'`): the caret sits at a sum of these
    // advances, so measured and painted ink have to agree (WMPCM-8161), and
    // `layoutShapeTextContent` does not measure one character at a time on every path.
    ctx.fontKerning = 'none'
    ctx.letterSpacing = letterSpacingCss(run?.tracking ?? 0, run?.kerning ?? 0, runPt)

    return ctx.measureText(value).width
  }

  return {
    measureTextPt,
    charRunMap,
    pointSize,
    pointPt,
    leadingMult,
    rowMetricPt,
    familyId,
    cssFamily,
    fontWeight,
    fontStyle,
  }
}
